import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import moment from 'moment'
import React, { useContext, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import AnDateField from '../../../../../components/DateTime/DateField'
import { DetailRow } from '../../../../../components/Cards/DetailsBox'
import { GlobalContext, UserContext } from '../../../../../App'
import StaffSelector from '../../../../events/StaffSelector'
import API from '../../../../../services/API'
import PresetsTextField from '../../../../../components/Forms/PresetsTextField'
import EmployeeSelector from '../../../../events/EmployeeSelector'

const CreateCDAudit = () => {
  const navigate = useNavigate()
  const theme = useTheme()
  const isLarge = useMediaQuery(theme.breakpoints.up('lg'))
  const { user } = useContext(UserContext)
  const { setToast } = useContext(GlobalContext)
  const { employeeID } = useParams()
  const [loading, setLoading] = useState(true)
  const [loadingError, setLoadingError] = useState(false)
  const [carriesCDs, setCarriesCDs] = useState(true)
  const [employee, setEmployee] = useState(null)
  const samePerson = user?.employee?._id === employeeID

  const [medicineName, setMedicineName] = useState('')
  const [auditDate, setAuditDate] = useState(moment())
  const [expectedQuantity, setExpectedQuantity] = useState('')
  const [actualQuantity, setActualQuantity] = useState('')
  const [allInDate, setAllInDate] = useState(false)
  const [inGoodCondition, setInGoodCondition] = useState(false)
  const [witnessedBy, setWitnessedBy] = useState(null)
  const [notes, setNotes] = useState('')

  // 'personal' audits a clinician's own CDs held outside AmbuNet (expected
  // quantity entered by hand). 'ambunet' audits tracked stock in their
  // assigned bags, with the expected quantity calculated from live stock.
  const [stockSource, setStockSource] = useState('personal')
  const [cdList, setCdList] = useState(null)
  const [selectedDrug, setSelectedDrug] = useState(null)
  const [stockInfo, setStockInfo] = useState(null)
  const [adjustManually, setAdjustManually] = useState(false)
  const [overrideReason, setOverrideReason] = useState('')

  useEffect(() => {
    if (!employeeID) return
    API.get('/medsmx/employee-cd-audits/info/' + employeeID)
      .then((res) => {
        if (res.status === 200) {
          setEmployee(res.data)
          if (res.data.carriesControlledDrugs !== true) {
            setCarriesCDs(false)
          }
        } else {
          setLoadingError(true)
        }
      })
      .catch(() => setLoading(true))
      .finally(() => setLoading(false))
  }, [employeeID])

  useEffect(() => {
    if (stockSource !== 'ambunet' || cdList) return
    API.get('/medsmx/controlleddruglist').then((res) => {
      if (res.status === 200) setCdList(res.data)
    })
  }, [stockSource, cdList])

  useEffect(() => {
    if (!selectedDrug || !employeeID) {
      setStockInfo(null)
      return
    }
    API.get(
      `/medsmx/employee-cd-stock/${employeeID}?description=${selectedDrug._id}`,
    ).then((res) => {
      if (res.status === 200) {
        setStockInfo(res.data)
        setExpectedQuantity(res.data.total)
        setAdjustManually(false)
        setOverrideReason('')
      }
    })
  }, [selectedDrug, employeeID])

  const handleSourceChange = (e, newSource) => {
    if (!newSource) return
    setStockSource(newSource)
    setSelectedDrug(null)
    setStockInfo(null)
    setMedicineName('')
    setExpectedQuantity('')
    setAdjustManually(false)
    setOverrideReason('')
  }

  const stockBreakdown = stockInfo
    ? [
        ...stockInfo.breakdown.map((row) => `${row.name}: ${row.qty}`),
        ...(stockInfo.prepared ? [`Prepared syringes: ${stockInfo.prepared}`] : []),
      ].join(' · ')
    : ''

  const handleSubmit = (e) => {
    e.preventDefault()
    API.post('/medsmx/employee-cd-audit', {
      medicineName:
        stockSource === 'ambunet' ? selectedDrug?.title ?? '' : medicineName,
      stockSource,
      ...(stockSource === 'ambunet' && {
        medicineDescription: selectedDrug?._id ?? null,
        calculatedQuantity: stockInfo?.total ?? null,
        overrideReason: adjustManually ? overrideReason : '',
        locationsCounted: stockInfo?.breakdown ?? [],
        preparedCounted: stockInfo?.prepared ?? 0,
      }),
      auditDate: auditDate.toDate(),
      expectedQuantity,
      actualQuantity,
      allInDate,
      inGoodCondition,
      witnessedBy: witnessedBy?._id ?? null,
      notes,
      employeeID,
    })
      .then((res) => {
        if (res.status === 200) {
          navigate(`/cd-audit/${res.data}`)
        } else {
          setToast({
            message: 'Unable to Submit Audit. Please try again later.',
            severity: 'error',
          })
        }
      })
      .catch((e) => {
        console.error(e)
        setToast({
          message: 'Unable to Submit Audit. Please try again later.',
          severity: 'error',
        })
      })
  }

  return (
    <Stack component={Paper} padding={1} gap={1}>
      <Typography variant="title2">Controlled Drug Audit</Typography>
      <Typography variant="title3">
        {`${employee?.userAccount?.firstName ?? ''} ${
          employee?.userAccount?.lastName ?? ''
        } ${employee?.clinicalRole ? `(${employee.clinicalRole})` : ''}`}
      </Typography>
      {loading ? (
        <LinearProgress />
      ) : loadingError ? (
        <Alert severity="error">
          Sorry, we're unable to load this form right now. Please contact
          support if the issue persists.
        </Alert>
      ) : !carriesCDs ? (
        <Alert severity="info">
          This Employee does not carry Controlled Drugs
        </Alert>
      ) : samePerson ? (
        <Alert severity="info">
          You cannot audit your own Controlled Drugs.
        </Alert>
      ) : (
        <Grid component="form" onSubmit={handleSubmit} spacing={2} container>
          <Grid item xs={12}>
            <ToggleButtonGroup
              value={stockSource}
              exclusive
              onChange={handleSourceChange}
              size="small"
              color="primary"
            >
              <ToggleButton value="personal">Personal CDs</ToggleButton>
              <ToggleButton value="ambunet">AmbuNet Stock</ToggleButton>
            </ToggleButtonGroup>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              mt={0.5}
            >
              {stockSource === 'personal'
                ? "Controlled drugs the clinician holds outside AmbuNet — enter the expected quantity from their own records."
                : 'Stock AmbuNet tracks in their assigned bags — the expected quantity is calculated from live stock.'}
            </Typography>
          </Grid>
          <Grid item xs={12} lg={8}>
            {stockSource === 'ambunet' ? (
              <Autocomplete
                options={cdList ?? []}
                loading={!cdList}
                value={selectedDrug}
                onChange={(e, newValue) => setSelectedDrug(newValue)}
                getOptionLabel={(option) =>
                  [option.title, option.presentation]
                    .filter(Boolean)
                    .join(' — ')
                }
                isOptionEqualToValue={(option, value) =>
                  option._id === value._id
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Controlled Drug"
                    required
                    helperText="Controlled drugs from your formulary"
                  />
                )}
              />
            ) : (
              <PresetsTextField
                value={medicineName}
                setValue={setMedicineName}
                label="Medicine Name"
                fullWidth
                required
                helperText="i.e. Morphine Sulphate 10mg/1ml"
                presets={[
                  'Morphine Sulphate 10mg/1ml',
                  'Morphine Sulphate Oral Suspension',
                  'Diazepam Rectal 2.5mg',
                  'Diazepam Rectal 5mg',
                  'Diazepam Rectal 10mg',
                  'Diazepam 5mg/1ml',
                  'Diazepam 10mg/2ml',
                  'Midazolam Buccal Liquid 10mg/2ml',
                  'Ketamine 10mg/ml',
                  'Ketamine 50mg/ml',
                ]}
              />
            )}
          </Grid>
          <Grid item xs={12} lg={4}>
            <AnDateField
              date={auditDate}
              onChange={(newDate) => setAuditDate(newDate)}
              label="Audit Date"
              fullWidth
            />
          </Grid>
          {stockSource === 'ambunet' && stockInfo && !stockInfo.hasBags && (
            <Grid item xs={12}>
              <Alert severity="warning">
                This employee has no drug bags assigned, so there is no
                AmbuNet-tracked stock to audit. Switch to Personal CDs if you
                are auditing drugs they hold outside AmbuNet.
              </Alert>
            </Grid>
          )}
          <Grid item xs={12} md={6} lg={3}>
            <TextField
              label="Expected QTY"
              type="number"
              value={expectedQuantity}
              onChange={(e) => setExpectedQuantity(Number(e.target.value))}
              fullWidth
              required
              InputProps={{
                readOnly: stockSource === 'ambunet' && !adjustManually,
              }}
              helperText={
                stockSource === 'ambunet' && stockInfo
                  ? stockBreakdown || 'No tracked stock found'
                  : undefined
              }
            />
            {stockSource === 'ambunet' && stockInfo && (
              <FormControlLabel
                label={
                  <Typography variant="caption">Adjust manually</Typography>
                }
                control={
                  <Checkbox
                    size="small"
                    checked={adjustManually}
                    onChange={(e) => {
                      setAdjustManually(e.target.checked)
                      if (!e.target.checked) {
                        setExpectedQuantity(stockInfo.total)
                        setOverrideReason('')
                      }
                    }}
                  />
                }
              />
            )}
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <TextField
              label="Actual QTY"
              type="number"
              value={actualQuantity}
              onChange={(e) => setActualQuantity(Number(e.target.value))}
              fullWidth
              required
            />
          </Grid>
          <Grid
            item
            xs={12}
            md={6}
            lg={3}
            justifyContent="center"
            alignItems="center"
            display="flex"
          >
            <FormControlLabel
              label="All In Date"
              control={
                <Checkbox
                  checked={allInDate}
                  onChange={(e) => setAllInDate(e.target.checked)}
                  color="anGreen"
                  sx={{
                    color: !allInDate ? 'error.main' : 'inherit',
                    '&.Mui-checked': {
                      color: 'anGreen.main', // or your preferred green
                    },
                  }}
                />
              }
              sx={{
                color: !allInDate ? 'error.main' : 'inherit',
                fontWeight: 'bold',
              }}
            />
          </Grid>
          <Grid
            item
            xs={12}
            md={6}
            lg={3}
            justifyContent="center"
            alignItems="center"
            display="flex"
          >
            <FormControlLabel
              label="In Good Condition"
              control={
                <Checkbox
                  checked={inGoodCondition}
                  onChange={(e) => setInGoodCondition(e.target.checked)}
                  color="anGreen"
                  sx={{
                    color: !inGoodCondition ? 'error.main' : 'inherit',
                    '&.Mui-checked': {
                      color: 'anGreen.main', // or your preferred green
                    },
                  }}
                />
              }
              sx={{
                color: !inGoodCondition ? 'error.main' : 'inherit',
                fontWeight: 'bold',
              }}
            />
          </Grid>
          <Grid item xs={12} lg={6} display="flex" alignItems="center">
            <DetailRow
              label="Completed By"
              value={`${user.firstName} ${user.lastName}`}
            />
          </Grid>
          <Grid item xs={12} lg={6}>
            <EmployeeSelector
              label="Witnessed By"
              value={witnessedBy}
              onChange={(e, newValue) => setWitnessedBy(newValue)}
              preventSelectingSelf
            />
          </Grid>
          {stockSource === 'ambunet' &&
            adjustManually &&
            Number(expectedQuantity) !== Number(stockInfo?.total) && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Reason for adjusting the expected quantity"
                  required
                  value={overrideReason}
                  onChange={(e) => setOverrideReason(e.target.value)}
                  helperText={`Calculated from live stock: ${stockInfo?.total}`}
                />
              </Grid>
            )}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Notes"
              required={
                !inGoodCondition ||
                !allInDate ||
                expectedQuantity !== actualQuantity
              }
              multiline
              minRows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              helperText={
                (!inGoodCondition ||
                  !allInDate ||
                  expectedQuantity !== actualQuantity) &&
                'Please describe the issue(s)'
              }
            />
          </Grid>
          <Grid item xs={12}>
            <Button variant="contained" fullWidth type="submit" color="success">
              Submit
            </Button>
          </Grid>
        </Grid>
      )}
    </Stack>
  )
}

export default CreateCDAudit
